import React from "react";
import { Check } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const plans = [{
  name: "Quick Check In",
  price: "$45",
  period: "/session",
  description: "15 to 20 minute consultation for simple questions, PSA updates, or quick guidance.",
  features: ["PSA result review", "Lab or report clarification", "Targeted Q&A", "Guidance on next steps"],
  cta: "Book Now",
  recommended: false,
  link: {
    to: "/PatientBookingPage"
  }
}, {
  name: "Follow Up Consultation",
  price: "$95",
  period: "/session",
  description: "30 to 45 minute consultation to review new results and adjust your plan as your situation evolves.",
  features: ["PSA trend analysis", "Lab and pathology review", "Updated risk assessment", "Continued guidance and Q&A"],
  cta: "Schedule Session",
  recommended: false,
  link: {
    to: "/PatientBookingPage"
  }
}, {
  name: "Focused Consultation",
  price: "$150",
  period: "/session",
  description: "A 60 minute session for patients who need deeper clarity on diagnosis, test results, or treatment options without a full comprehensive review.",
  features: ["Detailed discussion of diagnosis or test results", "Treatment option overview", "Clarification of key decision points", "Guidance on next steps"],
  cta: "Book Session",
  recommended: false,
  link: {
    to: "/PatientBookingPage"
  }
}, {
  name: "Navigator Starter Package",
  price: "$325",
  period: "",
  description: "Best starting point for patients early in the decision process who want deeper clarity and structured support.",
  features: ["1 Full Assessment consultation", "2 Follow Up sessions", "Full medical record review", "Treatment option discussion", "Guidance on next steps", "$385 total value"],
  cta: "Get Started",
  recommended: true,
  link: {
    to: "/PatientBookingPage"
  }
}, {
  name: "Ongoing Navigator Support",
  price: "$575",
  period: "",
  description: "Continued support for patients who want ongoing guidance through monitoring, treatment decisions, and follow up care.",
  features: ["1 Full Assessment consultation", "5 Follow Up sessions", "Priority scheduling", "Ongoing PSA and lab monitoring", "Support through treatment decisions", "$675 total value"],
  cta: "Choose Package",
  recommended: false,
  link: {
    to: "/PatientBookingPage"
  }
}];
export default function Pricing() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="6586" id="packages" ref={ref} className={cn("bg-muted text-foreground py-24 sm:py-32 transition-opacity duration-1000 bg-[radial-gradient(var(--color-primary/10)_1px,transparent_1px)] [background-size:32px_32px]", isVisible ? "opacity-100" : "opacity-0")}>
      <div className="container mx-auto px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center mb-16">
          <h2 className="text-primary font-semibold tracking-widest uppercase text-sm mb-3">
            Pricing & Packages
          </h2>

          <p className="mt-2 text-4xl font-serif font-medium tracking-tight text-foreground sm:text-5xl">
            Support That Fits Your Stage of the Journey
          </p>

          <p className="mt-6 text-lg leading-8 text-muted-foreground">
            Choose a single session for focused guidance or a package for deeper
            support and continuity across multiple decisions.
          </p>
        </div>

        <div className="mx-auto grid max-w-lg grid-cols-1 gap-8 md:grid-cols-2 xl:max-w-none xl:grid-cols-5 items-start">
          {plans.map((plan, i) => <div key={plan.name} data-index={i} className={cn("relative flex flex-col transition-all duration-300", plan.recommended)}>
              <Card className={cn("flex flex-col h-full border-0 transition-all duration-300 rounded-3xl", plan.recommended ? "bg-card shadow-[0_20px_50px_-12px_hsl(var(--chart-2)/0.3)] ring-1 ring-[hsl(var(--chart-2)/0.2)]" : "bg-card/50 shadow-lg hover:shadow-xl hover:bg-card")}>
                <CardHeader className="pb-8">
                  <div className="flex justify-between items-center gap-4">
                    <CardTitle className="text-xl font-medium text-foreground">
                      {plan.name}
                    </CardTitle>
                  </div>

                  <CardDescription className="mt-4 text-sm leading-6 min-h-[72px] text-muted-foreground">
                    {plan.description}
                  </CardDescription>

                  <div className="mt-6 flex items-baseline gap-x-1 text-foreground">
                    <span className="text-5xl font-serif text-foreground">
                      {plan.price}
                    </span>
                    {plan.period && <span className="text-sm font-semibold leading-6 text-muted-foreground">
                        {plan.period}
                      </span>}
                  </div>
                </CardHeader>

                <CardContent className="flex-1">
                  <ul role="list" className="space-y-4 text-sm leading-6 text-muted-foreground">
                    {plan.features.map((feature, featureIndex) => <li key={featureIndex} className="flex gap-x-3 items-start" data-index={featureIndex}>
                        <Check className={cn("h-5 w-5 flex-none stroke-2", plan.recommended ? "text-[hsl(var(--chart-2))]" : "text-muted-foreground/70")} aria-hidden="true" />
                        <span className="text-[hsl(215,16%,47%)]">{feature}</span>
                      </li>)}
                  </ul>
                </CardContent>

                <CardFooter className="pt-8">
                  <Button asChild className="w-full rounded-lg h-12 text-base transition-all duration-300 bg-secondary text-secondary-foreground hover:bg-secondary/80">
                    <Link className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium px-4 py-2 w-full rounded-lg h-12 text-base transition-all duration-300 cursor-pointer underline cursor-pointer underline cursor-pointer underline cursor-pointer underline cursor-pointer" {...plan.link}><Link {...plan.link}><Link {...plan.link}><Link {...plan.link}><Link to={plan.link?.to || "/NavigationServicesPage"}>
                      {plan.cta}
                    </Link></Link></Link></Link></Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}